import type { CallbackInterface } from 'recoil'

import { asyncSelectState } from './state'

import type {
  AsyncSelectState,
  FetchItemByIdFunction,
  FetchItemsByTermFunction,
  FetchItemsFunction,
  OptionEntry,
  OptionEntryValue,
} from './model'

export const onValueChangeCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (
    key: string,
    _value: OptionEntryValue | OptionEntryValue[] | undefined,
    fetchItems: FetchItemsFunction,
    fetchItemById: FetchItemByIdFunction,
  ) => {
    const { status, initialOptions, searchOptions } =
      await snapshot.getPromise<AsyncSelectState>(asyncSelectState(key))

    if (status === 'error') {
      return
    }

    set(asyncSelectState(key), (prev: AsyncSelectState) => ({
      ...prev,
      loading: true,
    }))

    try {
      if (status === 'initial') {
        const { items, totalItems } = await fetchItems()

        const tmp: OptionEntryValue[] = Array.isArray(_value)
          ? [..._value]
          : _value === undefined
            ? []
            : [_value]

        const toAdd = await Promise.all(
          tmp
            .filter((v) => v !== 0)
            .filter(
              (v) =>
                items.find(({ value: optValue }) => optValue === v) ===
                undefined,
            ) // se non fanno parte del set iniziale
            .map(fetchItemById), // loading singolo
        )
        const toAddClean = toAdd.filter((v) => v !== undefined) as OptionEntry[] // filtro i risultati null di getItemById

        set(asyncSelectState(key), (prev: AsyncSelectState) => ({
          ...prev,
          status: 'ready',
          initialOptions:
            toAddClean.length > 0 ? [...toAddClean, ...items] : items,
          initialOptionsCount: totalItems,
        }))
      }

      if (status === 'ready') {
        const tmp: OptionEntryValue[] = Array.isArray(_value)
          ? [..._value]
          : _value === undefined
            ? []
            : [_value]

        const toAddFromSearching = tmp
          .filter(
            (v) =>
              initialOptions.find(({ value: optValue }) => optValue === v) ===
              undefined,
          ) // elementi di value non presenti in initialOptions
          .map((v) =>
            searchOptions.find(({ value: optValue }) => optValue === v),
          ) // prelevo da serchingOptions
          .filter((v) => v !== undefined) as OptionEntry[]

        const tmp2: OptionEntry[] = [...initialOptions, ...searchOptions]

        const tmp3: OptionEntryValue[] = Array.isArray(_value)
          ? [..._value]
          : _value === undefined
            ? []
            : [_value]

        const toAddFromApi = await Promise.all(
          tmp3
            .filter((v) => v !== 0)
            .filter((v) => !tmp2.some((a) => a.value === v)) // elementi di value non presenti in initialOptions concat searchingOptions
            .map(fetchItemById), //  prelevo da API
        )

        const toAddFromApiClean = toAddFromApi.filter(
          (v) => v !== undefined,
        ) as OptionEntry[]

        const newOptions = [
          ...toAddFromApiClean,
          ...toAddFromSearching,
          ...initialOptions,
        ]
        //  dedupe
        const dedupedOptions = [
          ...new Map(newOptions.map((item) => [item.value, item])).values(),
        ]

        set(asyncSelectState(key), (prev: AsyncSelectState) => ({
          ...prev,
          initialOptions: dedupedOptions,
        }))
      }
    } catch (error) {
      console.error('### error', error)

      set(asyncSelectState(key), (prev: AsyncSelectState) => ({
        ...prev,
        status: 'error',
      }))
    }

    set(asyncSelectState(key), (prev: AsyncSelectState) => ({
      ...prev,
      loading: false,
    }))
  }

export const searchItemsCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (
    key: string,
    searchTerm: string,
    fetchItemsByTerm: FetchItemsByTermFunction,
  ) => {
    set(asyncSelectState(key), (prev: AsyncSelectState) => ({
      ...prev,
      loading: true,
      searching: true,
    }))

    try {
      const { items, totalItems } = await fetchItemsByTerm(searchTerm)

      set(asyncSelectState(key), (prev: AsyncSelectState) => ({
        ...prev,
        searchOptions: items,
        searchOptionsCount: totalItems,
      }))
    } catch (error) {
      console.error('### error', error)

      set(asyncSelectState(key), (prev: AsyncSelectState) => ({
        ...prev,
        status: 'error',
      }))
    }

    set(asyncSelectState(key), (prev: AsyncSelectState) => ({
      ...prev,
      loading: false,
    }))
  }

export const resetSearchCallback =
  ({ set }: CallbackInterface) =>
  async (key: string) => {
    set(asyncSelectState(key), (prev: AsyncSelectState) => ({
      ...prev,
      searching: false,
      searchOptionsCount: 0,
    }))
  }
